/*
 * Ticket-transfer drawer (PROJ-0078-WU-10 — design note
 * docs/current/DESIGN-TRANSFER-RECIPIENT-STEP-V1.md, boardroom dispatch
 * BOARDROOM-2026-09-27-191409, CONDITIONAL GO: contract-independent parts).
 *
 * `cmp.drawer`: right-side overlay on desktop, bottom sheet at phone width
 * (no horizontal scroll). Overlay conventions follow HelpPanel: role=dialog,
 * Escape closes, a ✕ close button — plus focus moves into the drawer on open
 * and returns to the opener on close.
 *
 * States: recipient → submitting → preview → (confirming → done) | lapsed |
 * error. The preview renders the server's `preview` VERBATIM (price
 * difference / transfer fee / processing fee / total) — the UI never
 * computes, offsets or sums a price or fee (BACKEND-V1: no downgrade-surplus
 * fee offset).
 *
 * CONFIRM (TICKETS-CONTRACT-TRANSFER-HOLD-V1 v3 <checkout_handoff>): enabled
 * ONLY when the hold's order.total_minor === 0 (isFreeHold — NOT
 * preview.zero_total) and calls complete-free. A paid hold keeps Confirm
 * DISABLED (see the paid CONTRACT GAP in src/data/transfers.ts). After a
 * successful complete-free the orders are refreshed (onCompleted); "Transfer
 * sent" is shown ONLY for outcome paid_and_switched — TRANSFER_SWITCH_LIVE is
 * off until recipient delivery (WU-06) is wired, so paid_transfer_not_switched
 * (and the ambiguous already_paid_noop) show an honest pending state. Closing
 * the drawer lets an unconfirmed hold lapse passively — no cancel call.
 */
import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react'
import { Button, Card, StatusTag } from '../../components/ui'
import { useShellChrome } from '../../components/Shell'
import { loadSession } from '../../auth/session'
import { ensureLiveSession, redirectToSignIn, type SessionHealth } from '../../auth/sessionExpiry'
import { ticketsApiBase } from '../../data/api'
import { supportMailtoHref } from '../../data/support'
import {
  completeFreeTransfer, createTransferHold, isFreeHold, transferErrorKind,
  type CompleteFreeOutcome, type TransferErrorKind, type TransferHoldResponse,
} from '../../data/transfers'
import { useLocale, type MessageKey } from '../../i18n/useLocale'
import { formatCountdown, formatMinor, holdRemainingMs, recipientProblem, type RecipientProblem } from './transferModel'

type Phase =
  | { kind: 'recipient' }
  | { kind: 'submitting' }
  | { kind: 'preview'; hold: TransferHoldResponse; recipient: string }
  | { kind: 'confirming'; hold: TransferHoldResponse; recipient: string }
  | { kind: 'done'; outcome: CompleteFreeOutcome; recipient: string }
  | { kind: 'lapsed' }
  | { kind: 'error'; error: Exclude<TransferErrorKind, 'lapsed'> }

const PROBLEM_KEY: Record<RecipientProblem, MessageKey> = {
  empty: 'transfer.recipient.err.empty',
  format: 'transfer.recipient.err.format',
  self: 'transfer.recipient.err.self',
}

// Copy per TICKETS-CONTRACT-TRANSFER-HOLD-V1 <errors>. Only 'auth' (a
// Bearer-path 401) offers sign-in; link and ownership failures never do.
const ERROR_KEY: Record<Exclude<TransferErrorKind, 'lapsed'>, MessageKey> = {
  unavailable: 'transfer.err.unavailable',
  not_allowed: 'transfer.err.notAllowed',
  rule_denied: 'transfer.err.ruleDenied',
  not_active: 'transfer.err.notActive',
  hold_pending: 'transfer.err.holdPending',
  auth: 'transfer.err.auth',
  link_invalid: 'transfer.err.linkInvalid',
  link_expired: 'transfer.err.linkExpired',
  invalid: 'transfer.err.invalid',
  unknown: 'transfer.err.unknown',
}

const eyebrow: CSSProperties = {
  fontFamily: "'Space Mono', monospace", fontSize: 9.5, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-dim)',
}

export interface TransferDrawerProps {
  /** Box-office ticket id being transferred (orders[].tickets[].id). */
  sourceTicketId: string
  /** REQUIRED dest_ticket_type_id — the source ticket's own ticket_type_id
   *  for a person transfer. Empty ⇒ the drawer refuses to hold. */
  destTicketTypeId: string
  /** Ticket type label for the drawer header (display only). */
  ticketLabel?: string
  /** The signed-in buyer's email — the "not yourself" client check. */
  buyerEmail?: string | null
  onClose: () => void
  /** Called once a free transfer has been completed (any success outcome) so
   *  the caller refreshes the orders read. */
  onCompleted?: () => void
  /** Injectable seams (tests). */
  base?: string | null
  idToken?: string | null
  fetchImpl?: typeof fetch
  sleep?: (ms: number) => Promise<void>
  /** Session probe/heal before confirm (tests inject; default ensureLiveSession). */
  ensureSession?: () => Promise<SessionHealth>
}

export function TransferDrawer({
  sourceTicketId, destTicketTypeId, ticketLabel, buyerEmail, onClose, onCompleted,
  base = ticketsApiBase(), idToken = loadSession()?.idToken ?? null, fetchImpl, sleep,
  ensureSession = ensureLiveSession,
}: TransferDrawerProps) {
  const { t } = useLocale()
  const { isMobile } = useShellChrome()
  const [phase, setPhase] = useState<Phase>({ kind: 'recipient' })
  const [email, setEmail] = useState('')
  const [problem, setProblem] = useState<RecipientProblem | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const mounted = useRef(true)
  // Re-entry guard: the Continue <Button> is an implicit submit inside the
  // form, so one click fires BOTH its onClick and the form's onSubmit — without
  // this the hold was POSTed twice (the second answering 409 hold_pending).
  const inFlight = useRef(false)

  // Focus in on open; back to the opener on close.
  useEffect(() => {
    mounted.current = true
    const opener = document.activeElement as HTMLElement | null
    ;(inputRef.current ?? panelRef.current)?.focus()
    return () => {
      mounted.current = false
      opener?.focus?.()
    }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  // Refocus the input whenever the recipient step (re)appears.
  useEffect(() => {
    if (phase.kind === 'recipient') inputRef.current?.focus()
  }, [phase.kind])

  const submit = async (e?: FormEvent) => {
    e?.preventDefault()
    const p = recipientProblem(email, buyerEmail)
    setProblem(p)
    if (p) return
    // No base, or no ticket_type_id to send (required by the contract) ⇒
    // never attempt a hold that tickets would reject.
    if (!base || !destTicketTypeId) {
      setPhase({ kind: 'error', error: 'unavailable' })
      return
    }
    if (!idToken) {
      setPhase({ kind: 'error', error: 'auth' })
      return
    }
    if (inFlight.current) return
    inFlight.current = true
    const recipient = email.trim()
    setPhase({ kind: 'submitting' })
    try {
      const hold = await createTransferHold(
        { sourceTicketId, destTicketTypeId, recipientEmail: recipient },
        { kind: 'bearer', idToken },
        base,
        fetchImpl,
      )
      if (mounted.current) setPhase({ kind: 'preview', hold, recipient })
    } catch (err) {
      if (!mounted.current) return
      const kind = transferErrorKind(err)
      setPhase(kind === 'lapsed' ? { kind: 'lapsed' } : { kind: 'error', error: kind })
    } finally {
      inFlight.current = false
    }
  }

  const confirm = async () => {
    if (phase.kind !== 'preview' || !isFreeHold(phase.hold) || !base || inFlight.current) return
    inFlight.current = true
    const { hold, recipient } = phase
    setPhase({ kind: 'confirming', hold, recipient })
    try {
      // tickets #289: complete-free needs the SAME caller proof as the hold.
      // The preview can stay open until the hold lapses, so the token captured
      // at render may have expired: probe/heal first (single-flight, clears
      // nothing) and read the CURRENT session token, not the render-time one.
      const health = await ensureSession()
      if (health === 'dead') {
        if (mounted.current) setPhase({ kind: 'error', error: 'auth' })
        return
      }
      const token = loadSession()?.idToken ?? idToken
      if (!token) {
        if (mounted.current) setPhase({ kind: 'error', error: 'auth' })
        return
      }
      const res = await completeFreeTransfer(hold.order.order_id, { kind: 'bearer', idToken: token }, base, fetchImpl, sleep)
      onCompleted?.()
      if (mounted.current) setPhase({ kind: 'done', outcome: res.outcome, recipient })
    } catch (err) {
      if (!mounted.current) return
      const kind = transferErrorKind(err)
      setPhase(kind === 'lapsed' ? { kind: 'lapsed' } : { kind: 'error', error: kind })
    } finally {
      inFlight.current = false
    }
  }

  const restart = () => {
    setProblem(null)
    setPhase({ kind: 'recipient' })
  }

  const panel: CSSProperties = isMobile
    ? {
        position: 'fixed', insetInline: 0, bottom: 0, maxHeight: '88vh', zIndex: 61,
        background: 'var(--bg-help)', borderTop: '1px solid var(--hairline-10)',
        borderRadius: '16px 16px 0 0', display: 'flex', flexDirection: 'column',
        overflowX: 'hidden', boxShadow: '0 -12px 40px rgba(0, 0, 0, 0.45)',
      }
    : {
        position: 'fixed', top: 0, insetInlineEnd: 0, bottom: 0, width: 'min(420px, 100vw)', zIndex: 61,
        background: 'var(--bg-help)', borderInlineStart: '1px solid var(--hairline-10)',
        boxShadow: 'var(--shadow-help)', display: 'flex', flexDirection: 'column', minHeight: 0,
      }

  return (
    <>
      <div data-testid="transfer-scrim" aria-hidden onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0, 0, 0, 0.5)' }} />
      <div
        ref={panelRef}
        data-testid="transfer-drawer"
        data-phase={phase.kind}
        role="dialog"
        aria-modal="true"
        aria-labelledby="transfer-title"
        tabIndex={-1}
        style={panel}
      >
        <div style={{ flex: 'none', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '16px 18px 13px', borderBottom: '1px solid var(--hairline-08)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...eyebrow, marginBottom: 3 }}>{t('transfer.drawer.eyebrow')}</div>
            <div id="transfer-title" style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.02em' }}>{t('transfer.drawer.title')}</div>
            {ticketLabel && (
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4, overflowWrap: 'anywhere' }}>{ticketLabel}</div>
            )}
          </div>
          <button
            data-testid="transfer-close"
            aria-label={t('transfer.drawer.close')}
            title={t('transfer.drawer.close')}
            onClick={onClose}
            style={{
              flex: 'none', minWidth: 'var(--tap-min)', minHeight: 'var(--tap-min)', borderRadius: 11,
              border: '1px solid var(--hairline-12)', background: 'transparent',
              color: 'var(--text-secondary)', fontFamily: 'inherit', fontSize: 15, cursor: 'pointer',
            }}
          >✕</button>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '16px 18px 20px' }}>
          {(phase.kind === 'recipient' || phase.kind === 'submitting') && (
            <form noValidate onSubmit={(e) => { void submit(e) }} className="col" style={{ gap: 10 }}>
              <label htmlFor="transfer-email" style={{ fontSize: 12.5, fontWeight: 800 }}>{t('transfer.recipient.label')}</label>
              <input
                ref={inputRef}
                id="transfer-email"
                data-testid="transfer-email"
                type="email"
                inputMode="email"
                autoComplete="off"
                value={email}
                disabled={phase.kind === 'submitting'}
                aria-invalid={problem ? true : undefined}
                aria-describedby={problem ? 'transfer-email-err' : 'transfer-email-help'}
                onChange={(e) => { setEmail(e.target.value); setProblem(null) }}
                style={{
                  width: '100%', boxSizing: 'border-box', minHeight: 44, padding: '0 12px',
                  borderRadius: 'var(--tru-r-btn)', border: `1px solid ${problem ? 'var(--tru-status-error-border-60)' : 'var(--tru-border-14)'}`,
                  background: 'var(--tru-surface-1)', color: 'var(--tru-text-primary)', fontFamily: 'inherit', fontSize: 14,
                }}
              />
              {problem ? (
                <p id="transfer-email-err" data-testid="transfer-email-error" role="alert" style={{ margin: 0, fontSize: 12, color: 'var(--tru-status-error)' }}>
                  {t(PROBLEM_KEY[problem])}
                </p>
              ) : (
                <p id="transfer-email-help" className="secondary" style={{ margin: 0, fontSize: 12 }}>{t('transfer.recipient.help')}</p>
              )}
              {phase.kind === 'submitting' ? (
                <p role="status" data-testid="transfer-submitting" className="secondary" style={{ margin: 0, fontSize: 12.5 }}>{t('transfer.submitting')}</p>
              ) : (
                <Button full onClick={() => { void submit() }}>{t('transfer.recipient.continue')}</Button>
              )}
            </form>
          )}

          {(phase.kind === 'preview' || phase.kind === 'confirming') && (
            <PreviewView
              hold={phase.hold}
              recipient={phase.recipient}
              confirming={phase.kind === 'confirming'}
              onConfirm={() => { void confirm() }}
              onLapse={() => setPhase((cur) => (cur.kind === 'preview' ? { kind: 'lapsed' } : cur))}
            />
          )}

          {phase.kind === 'done' && (
            <Card>
              <div className="col" data-testid="transfer-done" data-outcome={phase.outcome} role="status" style={{ padding: 16, gap: 12 }}>
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, overflowWrap: 'anywhere' }}>
                  {phase.outcome === 'paid_and_switched'
                    ? t('transfer.done.sent', { email: phase.recipient })
                    : t('transfer.done.pending')}
                </p>
                <Button variant="outline" onClick={onClose}>{t('transfer.done.close')}</Button>
              </div>
            </Card>
          )}

          {phase.kind === 'lapsed' && (
            <Card>
              <div className="col" data-testid="transfer-lapsed" role="status" style={{ padding: 16, gap: 12 }}>
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700 }}>{t('transfer.lapsed')}</p>
                <Button variant="outline" onClick={restart}>{t('transfer.restart')}</Button>
              </div>
            </Card>
          )}

          {phase.kind === 'error' && (
            <ErrorView kind={phase.error} sourceTicketId={sourceTicketId} onRestart={restart} />
          )}
        </div>
      </div>
    </>
  )
}

function PreviewView({ hold, recipient, confirming, onConfirm, onLapse }: {
  hold: TransferHoldResponse
  recipient: string
  confirming: boolean
  onConfirm: () => void
  onLapse: () => void
}) {
  const { t } = useLocale()
  const [now, setNow] = useState(() => Date.now())
  const remaining = holdRemainingMs(hold.transfer.hold_expires_at, now)

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  useEffect(() => {
    if (remaining === 0) onLapse()
  }, [remaining, onLapse])

  const p = hold.preview
  const free = isFreeHold(hold)
  // Rows EXACTLY as the design note lists them — the server's integers,
  // formatted only. Nothing is summed, offset or recomputed here.
  const rows: Array<{ key: string; label: MessageKey; minor: number }> = [
    { key: 'delta', label: 'transfer.preview.row.delta', minor: p.delta_minor },
    { key: 'fee', label: 'transfer.preview.row.fee', minor: p.fee_minor },
    { key: 'processing', label: 'transfer.preview.row.processing', minor: p.processing_fee_minor },
  ]
  return (
    <div className="col" data-testid="transfer-preview" style={{ gap: 12 }}>
      <p style={{ margin: 0, fontSize: 13, overflowWrap: 'anywhere' }}>{t('transfer.preview.to', { email: recipient })}</p>
      {remaining !== null && (
        <div data-testid="transfer-countdown" role="timer" aria-live="off" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StatusTag text={t('transfer.preview.countdown', { time: formatCountdown(remaining) })} />
        </div>
      )}
      <Card>
        <dl style={{ margin: 0, padding: '6px 16px' }}>
          {rows.map((r) => (
            <div key={r.key} data-testid={`transfer-row-${r.key}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderBottom: '1px solid var(--hairline-06)' }}>
              <dt style={{ fontSize: 12.5, color: 'var(--tru-text-secondary)' }}>{t(r.label)}</dt>
              <dd style={{ margin: 0, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{formatMinor(r.minor, p.currency)}</dd>
            </div>
          ))}
          <div data-testid="transfer-row-total" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '11px 0 9px' }}>
            <dt style={{ fontSize: 13.5, fontWeight: 800 }}>{t('transfer.preview.row.total')}</dt>
            <dd style={{ margin: 0, fontSize: 13.5, fontWeight: 800, whiteSpace: 'nowrap' }}>{formatMinor(p.total_minor, p.currency)}</dd>
          </div>
        </dl>
      </Card>
      {/* Free (order.total_minor === 0) ⇒ complete-free. Paid ⇒ DISABLED:
          CONTRACT GAP — no create-intent inputs / Stripe key / 3DS / status
          endpoint on my.trumin (src/data/transfers.ts). */}
      {confirming ? (
        <p role="status" data-testid="transfer-confirming" className="secondary" style={{ margin: 0, fontSize: 12.5, textAlign: 'center' }}>
          {t('transfer.confirming')}
        </p>
      ) : (
        <Button full disabled={!free} onClick={free ? onConfirm : undefined}>{t('transfer.preview.confirm')}</Button>
      )}
      {!free && (
        <p data-testid="transfer-paid-soon" className="secondary" style={{ margin: 0, fontSize: 12, textAlign: 'center' }}>
          {t('transfer.preview.paidSoon')}
        </p>
      )}
    </div>
  )
}

function ErrorView({ kind, sourceTicketId, onRestart }: {
  kind: Exclude<TransferErrorKind, 'lapsed'>
  sourceTicketId: string
  onRestart: () => void
}) {
  const { t } = useLocale()
  const support = kind === 'unknown' ? supportMailtoHref({ ticketId: sourceTicketId }) : null
  return (
    <Card>
      <div className="col" data-testid="transfer-error" data-error-kind={kind} role="alert" style={{ padding: 16, gap: 12 }}>
        <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700 }}>
          {kind === 'unknown' ? (
            support ? (
              <>
                {t('transfer.err.unknown')}{' '}
                <a data-testid="transfer-support" href={support} style={{ color: 'var(--tru-status-teal-bright)', fontWeight: 700 }}>
                  {t('transfer.contactSupport')}
                </a>
              </>
            ) : t('transfer.err.unknownNoSupport')
          ) : t(ERROR_KEY[kind])}
        </p>
        {kind === 'auth' && (
          <Button variant="outline" onClick={() => redirectToSignIn()}>{t('transfer.err.authCta')}</Button>
        )}
        {(kind === 'invalid' || kind === 'unknown') && (
          <Button variant="outline" onClick={onRestart}>{t('transfer.restart')}</Button>
        )}
      </div>
    </Card>
  )
}
